import React, { useState } from 'react';
import { Button } from './button';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuTrigger,
	DropdownMenuItem,
} from './dropdown-menu';
import { Input } from './input';
import { RotateCcw } from 'lucide-react';

const predefinedColors = [
	// Row 1: Red spectrum
	{ name: 'Rot', value: '#dc2626' },
	{ name: 'Rot-Orange', value: '#ea580c' },
	{ name: 'Orange', value: '#f97316' },
	{ name: 'Gelb-Orange', value: '#f59e0b' },
	{ name: 'Gelb', value: '#eab308' },
	{ name: 'Gelbgrün', value: '#84cc16' },
	
	// Row 2: Green to Blue spectrum
	{ name: 'Grün', value: '#22c55e' },
	{ name: 'Blaugrün', value: '#10b981' },
	{ name: 'Teal', value: '#14b8a6' },
	{ name: 'Türkis', value: '#06b6d4' },
	{ name: 'Hellblau', value: '#0ea5e9' },
	{ name: 'Blau', value: '#3b82f6' },
	
	// Row 3: Blue to Gray spectrum
	{ name: 'Dunkelblau', value: '#1d4ed8' },
	{ name: 'Lila', value: '#8b5cf6' },
	{ name: 'Magenta', value: '#d946ef' },
	{ name: 'Rosa', value: '#ec4899' },
	{ name: 'Braun', value: '#a16207' },
	{ name: 'Grau', value: '#6b7280' },
	
	// Additional neutral colors
	{ name: 'Hellgrau', value: '#d3d3d3' },
	{ name: 'Weiss', value: '#ffffff' },
];

export interface ColorPickerProps {
	currentColor?: string;
	onColorSelect: (color: string | null) => void;
	disabled?: boolean;
	defaultColor?: string;
	showReset?: boolean;
	label?: string;
	placeholder?: string;
}

export const ColorPicker: React.FC<ColorPickerProps> = ({ 
	currentColor, 
	onColorSelect, 
	disabled = false,
	defaultColor = '#d3d3d3',
	showReset = true,
	label = 'Farbe wählen',
	placeholder
}) => {
	const [customHex, setCustomHex] = useState('');
	const [isOpen, setIsOpen] = useState(false);

	const displayColor = currentColor || defaultColor;

	const handleColorSelect = (color: string) => {
		onColorSelect(color);
		setIsOpen(false);
	};  

	const handleCustomColorSubmit = () => {
		if (customHex && /^#[0-9A-F]{6}$/i.test(customHex)) {
			onColorSelect(customHex);
			setCustomHex('');
			setIsOpen(false);
		}
	};

	const handleResetToDefault = () => {
		onColorSelect(null);
		setIsOpen(false);
	};

	return (
		<DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
			<DropdownMenuTrigger asChild>
				<Button 
					variant="ghost" 
					size="sm" 
					className="h-8 w-8 p-0 border"
					disabled={disabled}
				>
					<div 
						className="w-6 h-6 rounded-full border-2 border-white shadow-sm"
						style={{ backgroundColor: displayColor }}
						title={currentColor ? 'Benutzerdefinierte Farbe' : 'Standard Farbe'}
					/>
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end" className="w-80 p-4">
				<div className="space-y-2">
					<div className="text-sm font-medium">{label}</div>
					
					{/* Predefined Colors Grid */}
					<div className="grid grid-cols-6 gap-2">
						{predefinedColors.map((color) => (
							<DropdownMenuItem
								key={color.value}
								onClick={() => handleColorSelect(color.value)}
								className="p-0 h-8 flex items-center justify-center hover:bg-transparent focus:bg-transparent cursor-pointer"
							>
								<div 
									className={`w-6 h-6 rounded-full border-2 ${
										currentColor === color.value 
											? 'border-black ring-2 ring-offset-1 ring-gray-400' 
											: 'border-gray-300'
									}`}
									style={{ backgroundColor: color.value }}
									title={color.name}
								/>
							</DropdownMenuItem>
						))}
					</div>

					{/* Custom Hex Input */}
					<div className="pb-2">
						<label className="text-xs font-medium text-gray-600">
							Benutzerdefinierte Hex-Farbe
						</label>
						<div className="flex space-x-2">
							<Input
								type="text"
								value={customHex}
								onChange={(e) => setCustomHex(e.target.value.toUpperCase())}
								placeholder={placeholder?.toUpperCase() || displayColor?.toUpperCase()}
								className="h-8 text-sm font-mono"
								maxLength={7}
							/>
							<Button 
								size="sm" 
								onClick={handleCustomColorSubmit}
								disabled={!customHex || !/^#[0-9A-F]{6}$/i.test(customHex)}
								className="h-8"
							>
								OK
							</Button>
						</div>
					</div>          
				</div>

				{/* Reset to Default */}
				{showReset && (
					<div className="pt-2">
						<Button 
							variant="outline" 
							size="sm" 
							onClick={handleResetToDefault}
							className="w-full h-8 text-xs"
						>
							<RotateCcw className="w-3 h-3 mr-2" />
							Farbe zurücksetzen
						</Button>
					</div>
				)}
			</DropdownMenuContent>
		</DropdownMenu>
	);
};